/* =====================================================================================
 * TEMP DIAGNOSTIC — "Flower Bag: cart briefly shows 3 items / KES 4,600 after adding one"
 * (reported 2026-09-07). Captures the exact sequence of cart states around a cart mutation
 * so the transient wrong total/count can be seen settling. REMOVE once the race is found.
 *
 * Active on product + cart templates (where the report happens), or anywhere with `?fbdebug`
 * in the URL. Output is console.debug, prefixed `[fb-debug]` — filter the console by that.
 * ===================================================================================== */
import { StandardEvents } from '@shopify/events';

(() => {
  const FORCED = /[?&]fbdebug\b/.test(window.location.search);
  const templateName =
    (window.Theme && Theme.template && Theme.template.name) || document.documentElement.dataset.template || '';
  const ON = FORCED || templateName === 'cart' || templateName === 'product';
  if (!ON) return;

  const t0 = performance.now();
  const ts = () => `+${(performance.now() - t0).toFixed(0)}ms`;
  const log = (...args) => console.debug('[fb-debug]', ts(), ...args);

  const money = (cents) => {
    if (typeof cents !== 'number') return String(cents);
    return `KES ${(cents / 100).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;
  };

  /** Compact one cart's worth of lines for logging. */
  const summariseCart = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return cart;
    return {
      item_count: cart.item_count,
      total_price: money(cart.total_price),
      lines: cart.items.map((it) => ({
        title: it.title || it.product_title,
        qty: it.quantity,
        line_price: money(it.final_line_price ?? it.line_price),
        key: typeof it.key === 'string' ? it.key.slice(0, 12) : it.key,
        props: it.properties || {},
      })),
    };
  };

  const fetchCartSnapshot = async (label) => {
    try {
      const res = await fetch(`${(window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await res.json();
      log(`cart.js snapshot [${label}]`, summariseCart(cart));
    } catch (error) {
      log(`cart.js snapshot [${label}] FAILED`, error);
    }
  };

  // ---- fetch() interception --------------------------------------------------
  const CART_ROUTE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const nativeFetch = window.fetch.bind(window);
  let mutationSeq = 0;

  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isMutation = CART_ROUTE.test(url);
    const method = ((init && init.method) || (typeof input === 'object' && input && input.method) || 'GET').toUpperCase();

    if (!isMutation) return nativeFetch(input, init);

    const seq = ++mutationSeq;
    let body = (init && init.body) || null;
    try {
      if (typeof body === 'string') body = JSON.parse(body);
    } catch (_) {
      /* leave as-is */
    }
    log(`>>> #${seq} ${method} ${url}`, { requestBody: body });

    const started = performance.now();
    let response;
    try {
      response = await nativeFetch(input, init);
    } catch (error) {
      log(`<<< #${seq} ${method} ${url} NETWORK ERROR after ${(performance.now() - started).toFixed(0)}ms`, error);
      throw error;
    }

    response
      .clone()
      .json()
      .then((data) => {
        log(`<<< #${seq} ${method} ${url} (${response.status}, ${(performance.now() - started).toFixed(0)}ms)`, {
          status_field: data && data.status,
          errors: data && data.errors,
          summary: summariseCart(data),
          added_line: data && data.key ? { key: String(data.key).slice(0, 12), qty: data.quantity } : undefined,
        });
      })
      .catch(() => {
        /* non-JSON (sections-only text) — ignore */
      });

    // Watch the cart settle after this mutation.
    for (const delay of [300, 1200, 3000]) {
      setTimeout(() => fetchCartSnapshot(`#${seq} +${delay}ms`), delay);
    }

    return response;
  };

  // ---- cartLinesUpdate events ---------------------------------------------
  try {
    const eventName = StandardEvents && StandardEvents.cartLinesUpdate;
    if (eventName) {
      document.addEventListener(eventName, (event) => {
        const detail = event && event.detail;
        log(`event ${event.type}`, {
          action: detail && detail.action,
          itemCount: detail && detail.itemCount,
          source: detail && detail.source,
        });
        const promise = event && event.promise;
        if (promise && typeof promise.then === 'function') {
          promise
            .then((resolved) => {
              const d = resolved && resolved.detail;
              log(`event ${event.type} RESOLVED`, {
                itemCount: d && d.itemCount,
                source: d && d.source,
                summary: resolved && resolved.cart ? summariseCart(resolved.cart) : undefined,
              });
            })
            .catch((error) => log(`event ${event.type} REJECTED`, error));
        }
      });
      log(`listening for "${eventName}"`);
    }
  } catch (error) {
    log('could not attach cartLinesUpdate listener', error);
  }

  // ---- baseline snapshots ------------------------------------------------
  log(`armed on template="${templateName}"${FORCED ? ' (forced via ?fbdebug)' : ''}`);
  fetchCartSnapshot('page load');
  window.addEventListener('pageshow', (e) => fetchCartSnapshot(e.persisted ? 'pageshow (bfcache)' : 'pageshow'));
})();
